import States from "../../../../helpers/states";
import EventEmitter from "eventemitter3";

/**
 * Represent a bloc element in the wall that contains items.
 * New Blocs are created on wall navigation
 * @extends EventEmitter
 */
class WallBloc extends EventEmitter {


    /**
     * @constructor
     * @param {{x: Number, y: Number}} index Bloc index
     * @param {Object.Array<Object>} datas Blocs data
     * @param {WallManager} wall WallManager that controls blocs
     */
    constructor({
       index,
       datas,
       wall
    } = {}) {
        super();

        this.index = index;
        this.datas = datas;
        this.wall = wall;

        // image paths from datas
        this.dataImagePaths = [...this.datas.recipes_portrait.map(data => data.cover), this.datas.recipes_landscape.map(data => data.cover)];

        this.items = [];
        this.dom = {};
        this.position = {x:0, y: 0};

        // TODO: add to config
        this.itemDomSelector = '.wall-item';
        this.itemTypes = [
            {
                name: 'recipes_landscape',
                size: {x:1, y:1},
                template: 'item',
                count: 0
            },
            {
                name: 'recipes_portrait',
                size: {x:1, y:2},
                template: 'item',
                count: 0
            },
            {
                name: 'chefs',
                template: 'chef',
                size: {x:2, y:2},
                count: 0
            },
            {
                name: 'header',
                template: 'header',
                size: {x:2, y:2},
                count: 0
            }
        ];

        this.init();
    }

    /**
     * Bloc visibility
     * @type {Boolean}
     */
    get visible() {
        return this.dom.element.classList.has('visible');
    }

    set visible(v) {
        this.dom.element.classList[v ? 'add' :'remove']('visible');
    }

    /**
     * init bloc
     */
    init() {
        this.createDom();
        this._loadImages();
    }

    /**
     * create bloc dom with wall items inside
     */
    createDom() {
        this.dom.element = this._getItemFromTemplate('bloc');
        this.dom.element.gridIndex = this.index;

        this.setSize();
        this._fill();
        this.setPosition();
        this.dom.element.classList.add(`index_${this.index.x}_${this.index.y}`);

        // NOTE: to Debug -> this._fillText(this.dom.element, `${this.index.x}.${this.index.y}`);
    }

    /**
     * Set bloc position on wall
     */
    setPosition() {
        this.position.x = this.wall.blocSize.width * this.index.x;
        this.position.y = this.wall.blocSize.height * this.index.y;
        this.dom.element.style.left = this.position.x + 'px';
        this.dom.element.style.top = this.position.y + 'px';
    }

    /**
     * Set bloc size
     */
    setSize() {
        this.dom.element.style.width = this.wall.blocSize.width + 'px';
        this.dom.element.style.height = this.wall.blocSize.height + 'px';
    }

    /**
     * Update bloc size and children items
     */
    updateSize() {
        this.setSize();
        this.updateItemSizes();
        //this._resetItemFlow(bloc);
        this.pckry.layout();
        //bloc.pckry.reloadItems();
    }

    /**
     * Fill bloc with wall items
     * @private
     */
    _fill() {
        const length = this.wall.gridSize.x * this.wall.gridSize.y;
        this.dom.element.items = [];
        const itemType = this._generateItemTypeList(length);
        for (let i = 0; i < itemType.length; i++) {
            let type = this._getItemTypeBySize(itemType[i]);

            // NOTE : Specific - add header item if bloc 0.0
            if((this.index.x === 0 && this.index.y === 0) && itemType[i] === 4) {
                type = this.itemTypes.find(item => item.name === 'header');
            }
            const item = this._createItem(i + 1, type);
            this.dom.element.appendChild(item);
            this.items.push(item);
        }
    }

    /**
     * Get item type by its cells width & height size
     * @param {{x: Number, y: Number}} size Item size
     * @return {{x: Number, y: Number}}
     * @private
     */
    _getItemTypeBySize(size) {
        return this.itemTypes.find(item => {
            return  (item.size.x * item.size.y) === size
        });
    }

    /**
     * Generate an array of items type from bloc cells count,
     * this allows to fill perfectly the bloc with all items sizes
     * @param {int} cellCount Bloc cell count
     * @return {Array.<int>} generated item types array
     * @private
     */
    _generateItemTypeList(cellCount) {

        let datas = [];
        let defaultTallItemsPercent = 80;
        let itemPercentRange = 20;
        let percentRatio =  Math.round( itemPercentRange * ( Math.random() - 0.5 ) );
        let tallItemsPrecent = defaultTallItemsPercent + Math.round( itemPercentRange * ( Math.random() - 0.5 ) );
        let secondItemsPercent = 30 + Math.round( itemPercentRange * ( Math.random() - 0.5 ) );
        let hasFourCellsItem = this.index.x % 2 === 0 && this.index.y % 2 === 0;

        // set length minus item 4 if bloc has the four-cells Item
        // a bloc has only one four-cells item at the beginig-ng
        let smallItemsCellCount = hasFourCellsItem ? cellCount - 4 : cellCount;

        for (let i = 0, j = 0; j < smallItemsCellCount - 1; i++) {
            // Add type-2 items odd
            var typeIndex = i % 2 === 0 ?  1 : 2;

            // if length > secondItemsPercent add type-2 items even
            if((j * 100 / cellCount) > secondItemsPercent) {
                typeIndex = i % 2 === 0 ? 2 : 1;
            }

            // if length > tallItemsPrecent  add only type-1 items
            if((typeIndex * 100 / cellCount) > tallItemsPrecent ) {
                typeIndex = 1;
            }

            j += typeIndex;
            datas.push(typeIndex);
        }

        // random invert
        if ( Math.round(Math.random()) ) {
            datas.reverse();
        }

        // set last to be 1
        datas.pop();
        datas.push(1);

        // sum datas
        let sum = datas.reduce((accum, value) => {
            return accum + value;
        });

        // equalise datas length for grid length
        for (let i = 0; i < Math.abs(sum - smallItemsCellCount); i++) {
            if(sum > smallItemsCellCount) {
                datas.pop();
            } else if(sum < smallItemsCellCount) {
                datas.push(1);
            }
        }

        // Add 4 cells item at begin for even blocs
        if(hasFourCellsItem) {
            datas.unshift(4);
        }

        return datas;
    }

    /**
     * Set items positions with different sizes to fill the bloc entirely
     * @see [Packery]{@link https://git.io/vxWFK}
     */
    autoFlowItems() {
        this.pckry = new Packery( this.dom.element, {
            // options
            containerStyle: null,
            itemSelector: this.itemDomSelector,
            horizontal: true,
            percentPosition: true,
            resize: false,
            transitionDuration: 0
        });
    }

    /**
     * Reset item positioning in bloc
     * @private
     */
    _resetItemFlow() {
        this.pckry.destroy();
        this.autoFlowItems(this.dom.element);
    }

    /**
     * Create a bloc item from its template and data
     * @param {int} index Item index
     * @param {Object} type Item type data
     * @param {{x: Number, y: Number}} type.size Item type size
     * @param {String} type.name Item type name
     * @param {String} type.template Item type template id
     * @return {Node} item node element
     * @private
     */
    _createItem(index, type) {
        const sizeType = type.size;
        const dataType = type.name;
        const template = type.template;
        // get data
        const data = this._getItemData(type);
        // get item element from template
        let item = this._getItemFromTemplate(template, data);
        // set item size
        this._setItemSize(item, sizeType);
        // set classes
        item.className += ` index_${index} type-${type.name}`;

        // sepcific conditions on recipe item
        if(template === 'item') {
            item = this._setRecipeItemState(item, data, type, index);
        }

        // set type
        item.sizeFactor = sizeType;
        return item;
    }

    // Specific for recipe items

    /**
     * Set recipe item state and style from data
     * @param {Node} item node element
     * @param {Object} data Item data
     * @param {Object} type Item type data
     * @param {{x: Number, y: Number}} type.size Item type size
     * @param {String} type.name Item type name
     * @param {String} type.template Item type template id
     * @param {int} index Item index
     * @return {Node} item node element
     * @private
     */
    _setRecipeItemState(item, data, type, index) {

        const isLocked = data && data.is_private && !USER;
        const signatureClass = data && data.is_signature ? 'is-signature' : '';
        const privateClass = isLocked ? 'is-private' : '';
        const itemButton = item.querySelector('.ct--button-radius');
        const itemFavButton = item.querySelector('.ct--toggle-favorite');
        item.className += `${signatureClass} ${privateClass}`;
        // manage lock state
        if(isLocked) {
            itemButton.classList.add('js-call-login');
        } else {
            itemButton.setAttribute('href', data.url);
        }
        // manage favorite view
        if(data.is_favorite) {
            itemFavButton.classList.add('active')
        }
        return item;
    }

    /**
     * Get bloc item data from its type
     * @param {Object} type Item type data
     * @param {String} type.name Item type name
     * @param {int} type.count Item type count increment for type
     * @return {Object} item data
     * @private
     */
    _getItemData(type) {
        const dataArray = this.datas[type.name];
        if(!dataArray) {
            return;
        }
        if(dataArray.length === 0) {
            throw new Error('No data for type ' + '\"' + type.name + '\"');
        }
        const data = dataArray[type.count];
        // increment data count
        type.count += 1;
        if(dataArray.length - 1 < type.count) {
            type.count = 0;
        }
        return data;
    }

    /**
     * Set item size with size factor
     * @param {Node} item Node element
     * @param {{x: Number, y: Number}} sizeFactors Size multipliers
     * @private
     */
    _setItemSize(item, sizeFactors) {
        item.style.width = this.wall.cellSize.width * sizeFactors.x + 'px';
        item.style.height = this.wall.cellSize.height * sizeFactors.y + 'px';
    }

    /**
     * Re-set all item sizes
     */
    updateItemSizes() {
        this.items.forEach(item => {
            this._setItemSize(item, item.sizeFactor);
        });
    }

    /**
     * Generate node element with input data from js template
     * @param {String} templateId Template id
     * @param {Object} datas item input data
     * @return {Node} Generated node element
     * @private
     */
    _getItemFromTemplate(templateId, datas = null) {
        var html = this.wall.dom.templates[templateId].trim();
        if(datas) {
            html = this._fillWithData(html, datas);
        }
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        return wrapper.firstChild;
    }

    /**
     * Fill node element item with data
     * @param {Node} html
     * @param {Object} datas
     * @return {Node}
     * @private
     */
    _fillWithData(html, datas) {
        var regex = false;
        for (var data in datas) {
            regex = RegExp('tpl___var___tpl'.replace('var', data), 'g');
            html = html.replace(regex, datas[data]);
        }
        return html;
    }

    /**
     * Check if image is loaded
     * @param {String} path Image path
     * @private
     */
    _checkImage(path) {
        new Promise(resolve => {
            const img = new Image();
            img.onload = () => resolve({path, status: 'ok'});
            img.onerror = () => resolve({path, status: 'error'});

            img.src = path;
        });
    }

    /**
     * Return promise on bloc images loaded
     * @param {Arrax} paths Images path
     * @return {Promise<[]>}
     */
    _loadImages(...paths)  {
        Promise.all(paths.map(this._checkImage)).then(() => {
            this._handleImagesLoaded();
            this.emit('images:loaded');
        });
    };

    /**
     * Handle images loaded
     */
    _handleImagesLoaded() {
        this.visible = true;
    }

    /**
     * Fill the bloc text for debuging,
     * uncomment text bloc in html
     * @param {Node} element
     * @param {String} text
     * @private
     */
    _fillText(element, text) {
        const textNode = element.querySelector('.text');
        textNode.innerHTML = text;
    }

    /**
     * Add formated and colored console log for loggin wall status
     * @param {String} msg Message
     */
    setStatus(msg) {
        //console.log('%c- WallManager - ' + msg, "color:#00CBB7");
    }


}

export {WallBloc as default}



// WEBPACK FOOTER //
// ./src/js/pages/recipes/wall/WallManager/wallBloc.js